import qs from 'qs'
import request from '@/modules/base/util/request'
import { urls } from '@/modules/banking/config'
import config from '@/config'
import lang from '@/lang'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { FINANCE_API } from '@/modules/banking/constants'
import { getProcessIdHeader } from '@/modules/banking/_helpers/generateProcessId'

const { resourceHost } = config

/**
 * @typedef {Object} ChartOfAccountsListOptions
 * @property {string} [searchTerm] - Search the chart of accounts which contain searchTerm value. Searches in `accountNo`, `accountDescriptionLong`, `accountDescriptionShort`, `accountDescriptionCustom`.
 * @property {boolean} [favorite] - Set to `true` to get favorite accounts, `false` for non-favorite accounts, omit for both.
 * @property {boolean} [favoriteRevenueAccount] - Set to `true` to get favorite revenue accounts, `false` for non-favorite revenue accounts, omit for both.
 * @property {string} [accountCategory] - Filter by account category.
 * @property {string} [accountType] - Filter by account type. Available values: expenditures, assets, income, others, monetary, debtor, creditor, incomeExpenditures, invoice, economicAssetAccounting. Comma-separated values allowed for multiple types.
 * @property {string} [chartOfAccountsName] - Type of active chartOfAccounts for mapping purposes. Only needed when mapping is active and a searchTerm is provided. If not provided, default for application is used (OM and MB is SKR03 and MV is SKR49).
 * @property {'standard' | 'extended'} [visibility] - If omitted, both `standard` and `extended` are set.
 * @property {string} [orderBy] - Field to order by. Default is `accountNo`.
 * @property {string} [sortOrder] - Sort order. Default is "asc".
 * @property {boolean} [usedOnly] - If true, lists only the charts of accounts that have been used for any assignments.
 * @property {number} [offset] - Offset for pagination.
 * @property {number} [limit] - Limit for pagination. Default is 10000.
 */

/**
 * @typedef {Object} ChartOfAccountsListResponse
 * @property {Object} meta
 * @property {Object} data
 * @property {Object} data.meta
 * @property {number} data.meta.count
 * @property {Object} data.chartsOfAccount
 * @property {string} data.chartsOfAccount.name
 * @property {string} data.chartsOfAccount.description
 * @property {Array<ChartOfAccount>} data.chartsOfAccount.accounts
 */

/**
 * @typedef {Object} ChartOfAccount
 * @property {number} accountNo
 * @property {string} accountDescriptionLong
 * @property {string} accountDescriptionShort
 * @property {string} accountDescriptionCustom
 * @property {string} accountCategory
 * @property {string} accountType
 * @property {string} visibility
 * @property {boolean} favorite
 * @property {number} vatOption
 * @property {number} vatKey
 * @property {number} vatDefaultRate
 * @property {boolean} favoriteRevenueAccount
 * @property {AllowedVatRates} allowedVatRates
 * @property {boolean} imported
 * @property {string} id
 * @property {number} number
 * @property {Object.<string, number>} numbers
 * @property {string} descriptionLong
 * @property {string} descriptionShort
 * @property {string} descriptionCustom
 */

/**
 * @typedef {Object} AllowedVatRates
 * @property {Array<number>} values
 * @property {number} default
 */

export const BookingService = {
  /**
     * remove all assignments from specified booking
     *
     * @param bookingId number bookingId
    */
  removeAssignments: (bookingId) => {
    return new Promise((resolve, reject) => {

      return request(`${urls.resource}/booking/${bookingId}/assignments`, {
        method: 'DELETE',
        auth: true,
        isConcurrent: true,
      })
        .then(({ body }) => resolve(body))
        .catch(error => {
          let message = lang.bankingDeleteAssignmentsErrorMessage
          if (error && error.body && error.body.meta && error.body.meta.closingInvoice) {
            message = lang.bankingDeleteAssignmentsClosingInvoiceErrorMessage
          } else if (error && error.body && error.body.meta && error.body.meta.assignmentType && error.body.meta.assignmentType[0].code === 'NOT_ALLOWED') {
            message = lang.bankingDeleteAssignmentsNotAllowedErrorMessage
          }
          toast.showToast({ type: 'error', message })
          reject(error)
        })
    })
  },

  /**
     * create a new booking
     */
  createBooking: (data) => {
    return request(`${urls.resource}/booking`, {
      method: 'POST',
      auth: true,
      data,
    })
  },

  /**
     * delete specified booking
     *
     * @param bookingId number bookingId
    */
  deleteBooking: (bookingId) => {
    return request(`${urls.resource}/booking/${bookingId}`, {
      method: 'DELETE',
      auth: true,
    })
  },

  /**
     * get details of specified booking
     *
     * @param bookingId number bookingId
    */
  getDetails: (bookingId) => {
    return request(`${urls.resource}/booking/${bookingId}?expand=true`, {
      method: 'GET',
      auth: true,
    })
  },

  /**
   * update a booking (only reference at the moment)
   */
  updateBooking: (data) => {
    return request(`${urls.resource}/booking/${data.id}`, {
      method: 'PUT',
      auth: true,
      data,
    })
  },

  /**
     * get details of specified booking
     *
     * @param bookingId number bookingId
    */
  changeContactTanProcedure: (contactId, processId = null) => {
    return request(`${urls.resource}/contact/${contactId}/changetanprocedure`, {
      method: 'POST',
      auth: true,
      headers: {
        ...getProcessIdHeader(processId),
      },
    })
  },

  /**
     * create an internal transfer for the given booking with the given data
     *
     * @param bookingId number bookingId
     * @param data object data
    */
  createInternalTransfer: (bookingId, data) => {
    return request(`${urls.resource}/booking/${bookingId}/internaltransfer/`, {
      method: 'POST',
      auth: true,
      data,
    })
  },

  bankAccounts: {
    /**
        * reorder bankAccounts
        */
    reorder: (bankAccounts) => {
      const parsedBankAccounts = bankAccounts.map(bankAccount => ({ orderId: bankAccount.orderId, accountId: bankAccount.id }))
      return request(`${urls.resource}/bankAccounts/reorder`, {
        method: 'PUT',
        auth: true,
        data: { bankAccounts: parsedBankAccounts },
      })
    },
    /**
        * get active bank accounts
        */
    getAllActive: () => {
      return request(urls.fetchBankAccountList, {
        auth: true,
        isConcurrent: true,
      })
    },
  },

  transaction: {
    /**
         * delete single bookingAPI transaction by id
         */
    delete: (transactionId, processId) => {
      return request(`${urls.deleteTransaction(transactionId)}`, {
        method: 'DELETE',
        headers: { ...getProcessIdHeader(processId) },
        auth: true,
      })
    },

    deleteAll: () => {
      return request(`${urls.deleteTransactions}`, {
        method: 'DELETE',
        auth: true,
        data: { secret: 'garglkarg' },
      })
    },
  },

  agreements: {
    getAccepted: () => {
      return request(`${urls.resource}/agreements`, {
        method: 'GET',
        auth: true,
      })
    },

    getMissing: () => {
      return request(`${urls.resource}/agreements/missing`, {
        method: 'GET',
        auth: true,
      })
    },

    start: () => {
      return request(`${urls.resource}/agreements/start`, {
        method: 'GET',
        auth: true,
      })
    },
  },

  bankTransfer: {
    createByExpenseId: (expenseId, bankTransferRequestData, processId) => {
      return request(`${urls.resource}/banktransfer/expense/${expenseId}`, {
        method: 'POST',
        auth: true,
        headers: { ...getProcessIdHeader(processId) },
        data: { ...bankTransferRequestData },
      })
    },
    createByCreditNoteId: (creditNoteId, bankTransferRequestData, processId) => {
      return request(`${urls.resource}/banktransfer/creditnote/${creditNoteId}`, {
        method: 'POST',
        auth: true,
        headers: { ...getProcessIdHeader(processId) },
        data: { ...bankTransferRequestData },
      })
    },
    createByManualCreditNoteId: (manualCreditNoteId, bankTransferRequestData, processId) => {
      return request(`${urls.resource}/banktransfer/manualcreditnote/${manualCreditNoteId}`, {
        method: 'POST',
        auth: true,
        headers: { ...getProcessIdHeader(processId) },
        data: { ...bankTransferRequestData },
      })
    },
  },

  chartOfAccounts: {
    get: (id) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/${id}`, {
          auth: true,
          isConcurrent: true,
        })
          .then(({ body }) => resolve(body))
          .catch((e) => reject(e))
      })
    },
    /**
     * Get all bookkeeping accounts of the currently activated chart of accounts for a particular tenant.
     *
     * @param {ChartOfAccountsListOptions} options - Query options for filtering the chart of accounts.
     * @returns {Promise<ChartOfAccountsListResponse>} Resolves with the response body containing the chart of accounts.
     */
    getList: (options) => {
      return new Promise((resolve, reject) => {
        const qs = {
          limit: 10000,
          ...options,
        }

        return request(`${urls.resource}/chartofaccounts`, {
          auth: true,
          isConcurrent: true,
          data: qs,
        })
          .then(({ body }) => resolve(body))
          .catch((e) => reject(e))
      })
    },
    getListSetting: () => {
      return new Promise((resolve, reject) => {
        return request(`${resourceHost}setting/userlist/chartOfAccounts`, {
          method: 'GET',
          auth: true,
          isConcurrent: true,
        })
          .then(({ body }) => resolve(body))
          .catch((e) => reject(e))
      })
    },
    sortListSetting: (data) => {
      return new Promise((resolve, reject) => {
        if (data?.length) {
          return request(`${resourceHost}setting/userlist/chartOfAccounts/sort`, {
            method: 'PUT',
            auth: true,
            isConcurrent: true,
            data,
          })
            .then(({ body }) => resolve(body))
            .catch((e) => reject(e))
        } else {
          resolve({})
        }
      })
    },
    setColumnsListSetting: (data) => {
      return new Promise((resolve, reject) => {
        return request(`${resourceHost}setting/userlist/chartOfAccounts/columns`, {
          method: 'PUT',
          auth: true,
          isConcurrent: true,
          data,
        })
          .then(({ body }) => resolve(body))
          .catch((e) => reject(e))
      })
    },

    getInvoiceList: (queryParams, onlyGroupsWithDefaultRevenueAccount = true) => {
      const querystring = qs.stringify(queryParams)
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/invoice?${querystring}`, {
          qs: queryParams,
          auth: true,
          isConcurrent: true,
        })
          .then(({ body }) => {
            if (onlyGroupsWithDefaultRevenueAccount && body.data?.vatGroups) {
              body.data.vatGroups = body.data.vatGroups.filter(grp => grp.default > 0)
            }
            resolve(body)
          })
          .catch((e) => reject(e))
      })
    },

    getRevenueAccounts: () => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/revenue-accounts`, {
          auth: true,
          isConcurrent: true,
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },

    updateAccount: (id, data) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/${id}`, {
          auth: true,
          isConcurrent: true,
          method: 'PUT',
          data,
        })
          .then(({ body }) => resolve(body))
          .catch(e => reject(e))
      })
    },

    updateAccountVisibility: (id, visibility) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/${id}/visibility`, {
          auth: true,
          isConcurrent: true,
          method: 'PUT',
          data: { visibility },
        })
          .then(({ body }) => resolve(body))
          .catch(e => reject(e))
      })
    },

    updateAccountCustomName: (id, name) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/${id}/customname`, {
          auth: true,
          isConcurrent: true,
          method: 'PUT',
          data: { name },
        })
          .then(({ body }) => resolve(body))
          .catch(e => reject(e))
      })
    },

    updateAccountFavorite: (id, favorite) => {
      return request(`${urls.resource}/chartofaccounts/${id}/favorite`, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data: { favorite },
      })
    },

    updateRevenueAccount: (id, data) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/revenue-accounts/${id}`, {
          auth: true,
          method: 'PUT',
          data,
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },

    deleteRevenueAccount: (id) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/revenue-accounts/${id}`, {
          auth: true,
          method: 'DELETE',
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },

    createRevenueAccount: (data) => {
      return new Promise((resolve, reject) => {
        return request(`${urls.resource}/chartofaccounts/revenue-accounts`, {
          auth: true,
          method: 'POST',
          data,
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },

    createOrUpdateRevenueAccount: (data, currentTenantVatTypeOption) => {
      return new Promise((resolve, reject) => {
        data.currentTenantVatTypeOption = currentTenantVatTypeOption || FINANCE_API.REVENUE_ACCOUNT_VATTYPE_OPTIONS.ADD_CURRENT_TENANT_VATTYPE
        return request(`${urls.resource}/chartofaccounts/revenue-accounts/crud`, {
          auth: true,
          method: 'POST',
          data,
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },
  },

  taxPhrases: {
    getTaxPhrases: () => {
      return new Promise((resolve, reject) => {
        request(`${config.resourceHost}taxPhrases`, {
          auth: true,
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },
    updateTaxPhrase: (id, text) => {
      return new Promise((resolve, reject) => {
        request(`${config.resourceHost}taxPhrases/${id}`, {
          auth: true,
          data: { text },
          method: 'PUT',
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },
    createTaxPhrase: (text) => {
      return new Promise((resolve, reject) => {
        request(`${config.resourceHost}taxPhrases`, {
          auth: true,
          data: { text },
          method: 'POST',
        })
          .then(({ body }) => resolve(body))
          .catch((error) => reject(error))
      })
    },
  },

  logging: {
    add: (entry) => {
      return request(`${urls.resource}/logging`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: [entry],
      })
    },
  },

  cashJournal: {
    cancelBooking: (bookingId) => {
      return request(`${urls.resource}/cashjournal/cancel/${bookingId}`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
      })
    },
  },

  cashJournalPos: {
    privateAssignment: (bookingId, type) => {
      return request(`${resourceHost}pos/private-assignments/${bookingId}`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: {
          type,
        },
      })
    },
  },

  createCashlessAssigment: (bookingId, data) => {
    return request(`${urls.resource}/cashjournalpos/cashless-assigment/${bookingId}`, {
      method: 'POST',
      auth: true,
      data,
    })
  },

  deactivate: (id) => {
    return request(`${urls.resource}/bankaccount/status`, {
      auth: true,
      isConcurrent: true,
      method: 'POST',
      data: { accountActive: false, id },
    })
  },

  canBeDeleted: (id) => {
    return request(`${urls.resource}/bankaccount/${id}/has-assignments`, {
      auth: true,
      isConcurrent: true,
      method: 'GET',
    }).then(({ body }) => {
      return !body.data?.hasAssignments
    })
  },
}
